<template>
	<div class="false-air-wrapper">
		<!-- Single card: Filters + Summary + Pie -->
		<section class="fa-main-card">
			<div class="fa-main-card-header">
				<h1 class="fa-main-card-title">False Air</h1>
				<div class="fa-filters">
					<div class="fa-filter-group fa-filter-search">
						<span class="fa-filter-label">Frequency</span>
						<search
							:class="{ 'is-invalid': errors.frequency_id }"
							:custom-class="{ 'is-invalid': errors.frequency_id }"
							:initialize="filters.frequency_id"
							id="frequency_id"
							label="frequency_name"
							placeholder="Select Frequency"
							:data="frequencies"
							@input="onFaFrequencyInput"
						/>
						<span v-if="errors.frequency_id" class="invalid-feedback d-block">{{ errors.frequency_id[0] }}</span>
					</div>
					<div class="fa-filter-group">
						<span class="fa-filter-label">From</span>
						<input class="fa-filter-input" v-model="filters.from_date" type="date" placeholder="From" />
					</div>
					<div class="fa-filter-group">
						<span class="fa-filter-label">To</span>
						<input class="fa-filter-input" v-model="filters.to_date" type="date" placeholder="To" />
					</div>
					<button type="button" class="fa-filter-btn" @click="loadDashboard">
						<span class="fa-filter-btn-text">Generate</span>
					</button>
				</div>
			</div>
			<div class="fa-main-card-body">
				<div class="fa-summary-top">
					<div class="fa-summary-block fa-summary-single">
						<h2 class="fa-section-title">Summary</h2>
						<div class="fa-tiles fa-tiles-inline">
							<button type="button" class="fa-tile fa-tile-open fa-tile-clickable"
								:class="{ 'fa-tile-selected': faListActive === 'open' }" @click="selectFaList('open')">
								<span class="fa-tile-value">{{ openJobCount }}</span>
								<span class="fa-tile-label">Open</span>
							</button>
							<button type="button" class="fa-tile fa-tile-closed fa-tile-clickable"
								:class="{ 'fa-tile-selected': faListActive === 'closed' }" @click="selectFaList('closed')">
								<span class="fa-tile-value">{{ closedJobCount }}</span>
								<span class="fa-tile-label">Closed</span>
							</button>
							<button type="button" class="fa-tile fa-tile-total fa-tile-clickable" title="Hide pipeline list"
								@click="clearFaList">
								<span class="fa-tile-value">{{ totalJobs }}</span>
								<span class="fa-tile-label">Total</span>
							</button>
						</div>
					</div>
					<div class="fa-summary-fan-wrap" aria-label="L2 overall fan power loss">
						<h2 class="fa-section-title fa-section-title-spacer">&nbsp;</h2>
						<div class="fa-tile fa-tile-fan-power">
							<span
								class="fa-tile-value"
								:class="calculatedValueClass(overallFanPowerLossValue)"
							>
								{{ formatCalculatedValue(overallFanPowerLossValue) }}
							</span>
							<span class="fa-tile-label fa-tile-label-wrap">{{ overallFanPowerLossEquationName }}</span>
						</div>
					</div>
				</div>
				<div v-if="faListActive === 'open'" class="fa-chart-block">
					<h2 class="fa-section-title">Department wise deviations</h2>
					<div class="fa-pie-wrap">
						<Chart :highcharts="Highcharts" :options="pieChartOptions" />
					</div>
				</div>
			</div>
		</section>

		<!-- Equation hierarchy -->
		<section class="fa-hierarchy-card" aria-label="False air equation hierarchy">
			<div class="fa-hierarchy-head">
				<h2 class="fa-section-title mb-0">Hierarchy</h2>
			</div>
			<div class="fa-hierarchy-body">
				<div v-if="!faHierarchyTree.length" class="fa-empty fa-empty-tight">
					No calculation equations found for hierarchy.
				</div>
				<div v-else class="fa-org-chart">
					<div class="fa-org-node fa-org-node-root">
						<div class="fa-org-node-line">Overall</div>
						<div class="fa-org-node-line fa-org-node-sub">{{ faPlantCode }}</div>
						<div
							v-if="faHierarchyRootValue != null"
							class="fa-org-node-value"
							:class="calculatedValueClass(faHierarchyRootValue)"
						>
							{{ formatCalculatedValue(faHierarchyRootValue) }}
						</div>
					</div>

					<div class="fa-org-connector-down fa-org-connector-root"></div>

					<div class="fa-org-row fa-org-row-lines">
						<div
							v-for="line in faHierarchyTree"
							:key="line.key"
							class="fa-org-col"
						>
							<div class="fa-org-node">
								<div class="fa-org-node-line">{{ line.title }}</div>
								<div class="fa-org-node-line fa-org-node-sub">{{ line.subtitle }}</div>
								<div
									v-if="line.calculated_value != null"
									class="fa-org-node-value"
									:class="calculatedValueClass(line.calculated_value)"
								>
									{{ formatCalculatedValue(line.calculated_value) }}
								</div>
							</div>

							<div class="fa-org-connector-down"></div>

							<div class="fa-org-row fa-org-row-branches">
								<div
									v-for="branch in line.branches"
									:key="branch.key"
									class="fa-org-col fa-org-col-branch"
								>
									<div class="fa-org-node fa-org-node-branch">
										<div>{{ branch.label }}</div>
										<div
											v-if="branch.calculated_value != null"
											class="fa-org-node-value"
											:class="calculatedValueClass(branch.calculated_value)"
										>
											{{ formatCalculatedValue(branch.calculated_value) }}
										</div>
									</div>
									<div v-if="branch.children.length" class="fa-org-branch-tree">
										<ul class="fa-org-eq-list">
											<li
												v-for="child in branch.children"
												:key="child.label"
												class="fa-org-eq-li"
											>
												<FaOrgEquationNode :node="child" />
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Calculations (separate card / div) -->
		<section class="fa-calculations-card" aria-label="False air calculations">
			<div class="fa-calculations-head d-flex justify-content-between align-items-center flex-wrap gap-2">
				<h2 class="fa-section-title mb-0">Calculations</h2>
				<button
					type="button"
					class="btn btn-sm btn-primary fa-recalc-all-btn"
					:disabled="recalculatingAll || !!recalculatingFaStatId || !faEquationRows.length"
					@click="recalculateAllFaEquations"
				>
					<i class="fas fa-calculator me-1"></i>
					<span v-if="recalculatingAll">Recalculating all…</span>
					<span v-else>All</span>
				</button>
			</div>
			<div class="fa-calculations-body">
				<div class="table-responsive">
					<table class="table table-bordered table-striped mb-2">
						<thead>
							<tr>
								<th style="width: 56px">#</th>
								<th>Equation Name</th>
								<th style="width: 160px">Date</th>
								<th style="width: 160px">Calculated Value</th>
								<th class="text-center" style="width: 240px">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-if="faEquationRows.length === 0">
								<td colspan="5" class="text-center">No records found</td>
							</tr>
							<tr v-for="(row, idx) in faEquationRows" :key="'faeq-' + row.fa_stat_id">
								<td>{{ faEquationMeta.from + idx }}</td>
								<td>{{ row.equation_name }}</td>
								<td>{{ row.equation_date }}</td>
								<td>
									<span class="fa-calc-value" :class="calculatedValueClass(row.calculated_value)">
										{{ formatCalculatedValue(row.calculated_value) }}
									</span>
								</td>
								<td class="td-actions text-center">
									<div class="fa-calc-actions">
										<button
											type="button"
											class="btn btn-sm btn-outline-primary"
											:disabled="recalculatingAll || !!recalculatingFaStatId"
											@click="recalculateFaEquation(row)"
										>
											<i class="fas fa-calculator me-1"></i>
											<span v-if="recalculatingFaStatId === row.fa_stat_id">Recalculating…</span>
											<span v-else></span>
										</button>
										<button
											type="button"
											class="btn btn-sm btn-outline-secondary fa-calc-icon-btn"
											title="View graph"
											aria-label="View graph"
											:disabled="faEquationChartLoading && faEquationChartModal && faEquationChartModal.equation_name === row.equation_name"
											@click="openFaEquationChartModal(row)"
										>
											<i
												class="fas"
												:class="faEquationChartLoading && faEquationChartModal && faEquationChartModal.equation_name === row.equation_name ? 'fa-spinner fa-spin' : 'fa-chart-bar'"
											></i>
										</button>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="d-flex justify-content-end">
					<Pagination
						class="pagination pagination-sm mb-0"
						:maxPage="faEquationMeta.maxPage"
						:totalPages="faEquationMeta.lastPage"
						:currentPage="faEquationMeta.currentPage"
						@pagechanged="onFaEquationPageChange"
					/>
				</div>
			</div>
		</section>

		<!-- Month calendar strip: remaining open templates + deviations per day (click = Open for that day) -->
		<section class="fa-month-strip-card" aria-label="Month day breakdown">
			<div class="fa-month-strip-head">
				<h2 class="fa-section-title fa-month-strip-title">{{ faMonthStripTitle }}</h2>
				<p class="fa-month-strip-sub">
					<span class="fa-month-strip-kpi">Total inspection templates: <strong>{{ faMonthTotalTemplates }}</strong></span>
				</p>
			</div>
			<p v-if="faMonthTrendLoading" class="fa-month-strip-loading">Loading month…</p>
			<div v-else-if="!(faMonthStripDays && faMonthStripDays.length)" class="fa-empty fa-empty-tight">No inspection templates for this plant and filters.</div>
			<div v-else class="fa-month-strip-scroll">
				<template v-for="day in faMonthStripDaysUptoToday" :key="day.date">
					<button v-if="faIsMonthStripDayClickable(day)" type="button" class="fa-month-day-col"
						:class="{
							'fa-month-day-col-today': isFaLocalToday(day.date),
							'fa-month-day-col-selected': isFaMonthStripDaySelected(day.date),
						}"
						:title="faMonthStripDayTitle(day)" @click="onFaMonthStripDayClick(day)">
						<span class="fa-month-day-dow">{{ faWeekdayShort(day.date) }}</span>
						<span class="fa-month-day-num">{{ day.day }}</span>
						<span class="fa-month-day-remain" aria-label="Templates not fully closed through this day">{{ day.remaining_open }}</span>
						<span class="fa-month-day-dev" aria-label="Deviations that day">{{ day.deviated_count }}</span>
					</button>
					<div v-else class="fa-month-day-col fa-month-day-col-static" :title="faMonthDayTooltip(day)">
						<span class="fa-month-day-dow">{{ faWeekdayShort(day.date) }}</span>
						<span class="fa-month-day-num">{{ day.day }}</span>
						<span class="fa-month-day-remain" aria-label="Templates not fully closed through this day">{{ day.remaining_open }}</span>
						<span class="fa-month-day-dev" aria-label="Deviations that day">{{ day.deviated_count }}</span>
					</div>
				</template>
			</div>
		</section>

		<!-- Four-stage pipeline (click Open or Closed on summary) -->
		<section v-if="faListActive" class="fa-pipeline-section">
			<div class="fa-pipeline-card">
				<div class="fa-pipeline-header">
					<h2 class="fa-section-title">
						{{ pipelineSectionTitle }}
					</h2>
					<div class="fa-pipeline-header-right">
						<span v-if="faListActive === 'closed'" class="fa-pipeline-days-pill">Days</span>
						<button type="button" class="fa-pipeline-close" @click="clearFaList">Close list</button>
					</div>
				</div>
				<div class="fa-pipeline-legend">
					<span class="fa-pipeline-legend-item">
						<span class="fa-pipeline-legend-dot fa-pipeline-legend-done"></span> Completed
						</span>
					<span class="fa-pipeline-legend-item">
						<span class="fa-pipeline-legend-dot fa-pipeline-legend-pending"></span> Pending
					</span>
					<span class="fa-pipeline-legend-item">
						<span class="fa-pipeline-legend-dot fa-pipeline-legend-remain"></span> Remaining
					</span>
					</div>
				<div
					v-if="faListActive && !faPipelineLoading && !pipelineListEmpty"
					class="fa-pipeline-stage-head-row">
					<div class="fa-pipeline-job fa-pipeline-stage-head-spacer"></div>
					<div class="fa-pipeline-track fa-pipeline-track-headings">
						<div
							v-for="(label, idx) in faPipelineStageHeadings"
							:key="'fa-ph-' + idx"
							class="fa-pipeline-seg-equal fa-pipeline-heading-cell">
							{{ label }}
						</div>
					</div>
					<div class="fa-pipeline-total fa-pipeline-heading-total"></div>
				</div>
				<p v-if="faPipelineLoading" class="fa-pipeline-loading">Loading…</p>
				<p v-else-if="pipelineListEmpty" class="fa-empty">No templates in this view.</p>
				<!-- Closed: completed jobs only — four-stage bar + total days -->
				<div v-else-if="faListActive === 'closed'" class="fa-pipeline-body">
					<div v-for="group in faPipelineGroups" :key="'g-' + group.template_id + '-' + group.base_job_no"
						class="fa-pipeline-group">
						<div v-for="line in group.lines" :key="'l-' + line.user_template_id"
							class="fa-pipeline-row fa-pipeline-row-closed fa-pipeline-row-clickable"
							:class="{ 'fa-pipeline-row-rework': line.is_rework }"
							@click="openFaDetailModalFromLine(line, group)">
							<div class="fa-pipeline-job">
								<span class="fa-pipeline-id" :class="closedPipelineJobClass(line)">{{ line.display_job_no }}</span>
								<span v-if="!line.is_rework" class="fa-pipeline-name">{{ group.template_name }}</span>
								<span v-else class="fa-pipeline-name fa-pipeline-rework-label">Re-work</span>
								</div>
							<div class="fa-pipeline-track fa-pipeline-track-bar fa-pipeline-track-bar-closed">
								<div v-for="st in line.stages" :key="st.key" class="fa-pipeline-seg fa-pipeline-seg-equal fa-pipeline-seg-interactive"
									:class="'fa-pipeline-cell-' + st.state"
									@mouseenter="onStageHover($event, st)"
									@mousemove="moveStageTooltip($event)"
									@mouseleave="hideStageTooltip"
									@click.stop="openFaDetailModalFromLine(line, group)">
									<span class="fa-pipeline-seg-days">{{ openStageDayCount(st) }}d</span>
									<span class="fa-pipeline-seg-state">{{ stageStateLabel(st.state) }}</span>
									<span v-if="st.completed_on" class="fa-pipeline-seg-date">{{ st.completed_on }}</span>
								</div>
							</div>
							<div class="fa-pipeline-total">{{ line.total_delay_days }}d</div>
					</div>
					</div>
				</div>
				<!-- Open: in-progress jobs (grouped) + not submitted (flat rows) -->
				<div v-else class="fa-pipeline-body">
					<div v-for="group in faPipelineGroups" :key="'og-' + group.template_id + '-' + group.base_job_no"
						class="fa-pipeline-group">
						<div v-for="line in group.lines" :key="'ol-' + line.user_template_id"
							class="fa-pipeline-row fa-pipeline-row-closed fa-pipeline-row-clickable"
							:class="{ 'fa-pipeline-row-rework': line.is_rework }"
							@click="openFaDetailModalFromLine(line, group)">
							<div class="fa-pipeline-job">
								<span class="fa-pipeline-id fa-pipeline-id-open">{{ line.display_job_no }}</span>
								<span v-if="!line.is_rework" class="fa-pipeline-name">{{ group.template_name }}</span>
								<span v-else class="fa-pipeline-name fa-pipeline-rework-label">Re-work</span>
							</div>
							<div class="fa-pipeline-track fa-pipeline-track-bar fa-pipeline-track-bar-closed">
								<div v-for="st in line.stages" :key="st.key" class="fa-pipeline-seg fa-pipeline-seg-equal fa-pipeline-seg-interactive"
									:class="'fa-pipeline-cell-' + st.state"
									@mouseenter="onStageHover($event, st)"
									@mousemove="moveStageTooltip($event)"
									@mouseleave="hideStageTooltip"
									@click.stop="openFaDetailModalFromLine(line, group)">
									<span class="fa-pipeline-seg-days">{{ openStageDayCount(st) }}d</span>
									<span class="fa-pipeline-seg-state">{{ stageStateLabel(st.state) }}</span>
									<span v-if="st.completed_on" class="fa-pipeline-seg-date">{{ st.completed_on }}</span>
								</div>
							</div>
							<div class="fa-pipeline-total">{{ line.total_delay_days }}d</div>
						</div>
					</div>
					<div v-for="(row, rowIdx) in faPipelineRows" :key="row.template_id + '-' + (row.user_template_id || 'open')"
						class="fa-pipeline-row fa-pipeline-row-open fa-pipeline-row-clickable" :style="{ animationDelay: (rowIdx * 0.05) + 's' }"
						@click="openFaDetailModalFromRow(row)">
						<div class="fa-pipeline-job">
							<span class="fa-pipeline-id fa-pipeline-id-open">{{ row.job_no }}</span>
							<span class="fa-pipeline-name">{{ row.template_name }}</span>
						</div>
						<div class="fa-pipeline-track fa-pipeline-track-bar fa-pipeline-track-bar-open">
							<div v-for="st in row.stages" :key="st.key" class="fa-pipeline-seg fa-pipeline-seg-equal fa-pipeline-seg-interactive"
								:class="'fa-pipeline-cell-' + st.state"
								@mouseenter="onStageHover($event, st)"
								@mousemove="moveStageTooltip($event)"
								@mouseleave="hideStageTooltip"
								@click.stop="openFaDetailModalFromRow(row)">
								<span class="fa-pipeline-seg-days">{{ openStageDayCount(st) }}d</span>
								<span class="fa-pipeline-seg-state">{{ stageStateLabel(st.state) }}</span>
								<span v-if="st.completed_on" class="fa-pipeline-seg-date">{{ st.completed_on }}</span>
							</div>
						</div>
						<div class="fa-pipeline-total">{{ openRowTotalDays(row) }}d</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Stage hover tooltip -->
		<div
			v-if="faStageTooltip && faStageTooltip.visible"
			class="fa-stage-tooltip"
			:style="{ left: faStageTooltip.x + 'px', top: faStageTooltip.y + 'px' }"
			role="tooltip"
		>
			<div v-if="faStageTooltip.heading" class="fa-stage-tooltip-heading">{{ faStageTooltip.heading }}</div>
			<div v-for="(line, idx) in faStageTooltip.lines" :key="'tt-' + idx" class="fa-stage-tooltip-line">{{ line }}</div>
		</div>

		<!-- False Air equation chart modal -->
		<Teleport to="body">
			<div class="modal fade fa-equation-chart-modal" id="faEquationChartModal" tabindex="-1" aria-labelledby="faEquationChartModalLabel" aria-hidden="true">
				<div class="modal-dialog modal-xl modal-dialog-centered fa-equation-chart-modal-dialog">
					<div class="modal-content" v-if="faEquationChartModal">
						<div class="modal-header py-2">
							<h5 class="modal-title" id="faEquationChartModalLabel">Calculation Trend</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeFaEquationChartModal"></button>
						</div>
						<div class="modal-body fa-equation-chart-modal-body">
							<p v-if="faEquationChartLoading || !faEquationChartReady" class="text-center text-muted mb-0 py-5">Loading chart…</p>
							<div v-else-if="!(faEquationChartModal.points && faEquationChartModal.points.length)" class="fa-empty fa-empty-tight py-4">
								No calculated values found for this equation.
							</div>
							<div v-else ref="faEquationChartContainer" class="fa-equation-chart-container"></div>
						</div>
						<div class="modal-footer py-2">
							<button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal" @click="closeFaEquationChartModal">Close</button>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- False Air job detail modal -->
		<div class="modal fade" id="faDetailModal" tabindex="-1" aria-labelledby="faDetailModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg modal-dialog-scrollable">
				<div class="modal-content" v-if="faDetailModal">
					<div class="modal-header">
						<h5 class="modal-title" id="faDetailModalLabel">False Air — {{ faDetailModal.display_job_no }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeFaDetailModal"></button>
					</div>
					<div class="modal-body">
						<div class="fa-detail-summary mb-3">
							<div class="fa-detail-row">
								<span class="fa-detail-label">Template</span>
								<span class="fa-detail-value">{{ faDetailModal.template_name || '—' }}</span>
							</div>
							<div v-if="faDetailModal.is_rework" class="fa-detail-row">
								<span class="fa-detail-label">Type</span>
								<span class="fa-detail-value">Re-work</span>
							</div>
							<div class="fa-detail-row">
								<span class="fa-detail-label">Total delay</span>
								<span class="fa-detail-value">{{ faDetailModal.total_delay_days }}d</span>
							</div>
						</div>
						<div class="table-responsive">
							<table class="table table-bordered table-sm mb-0 fa-detail-table">
								<thead>
									<tr>
										<th>Stage</th>
										<th>Days</th>
										<th>Status</th>
										<th>Completed on</th>
										<th>Deviations</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(st, idx) in faDetailModal.stages" :key="'fa-ds-' + (st.key || idx)">
										<td>{{ faStageDisplayLabel(st, idx) }}</td>
										<td>{{ openStageDayCount(st) }}d</td>
										<td>
											<span class="fa-detail-status" :class="'fa-detail-status-' + st.state">{{ stageStateLabel(st.state) }}</span>
										</td>
										<td>{{ st.completed_on || '—' }}</td>
										<td>
											<template v-if="Number(st.deviated_count) > 0">
												{{ st.deviated_count }}
												<template v-if="faPipelineStageDeviationEquipment(st)">
													<br><small class="text-muted">{{ faPipelineStageDeviationEquipment(st) }}</small>
												</template>
											</template>
											<template v-else>—</template>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal" @click="closeFaDetailModal">Close</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Highcharts from 'highcharts'
import { Chart } from 'highcharts-vue'
import moment from 'moment'
import { Modal } from 'bootstrap'
import Search from '@/components/Search.vue'
import Pagination from '@/components/Pagination.vue'
import FaOrgEquationNode from '@/components/FaOrgEquationNode.vue'

function resolveHighchartsLocale() {
	const candidates = [
		document.documentElement && document.documentElement.lang,
		typeof navigator !== 'undefined' ? navigator.language : '',
		'en-US',
	]
	for (let i = 0; i < candidates.length; i++) {
		const raw = candidates[i]
		if (!raw || !String(raw).trim()) {
			continue
		}
		const locale = String(raw).trim().replace(/_/g, '-')
		try {
			new Intl.NumberFormat(locale)
			return locale
		} catch (e) {
			// try next candidate
		}
	}
	return 'en-US'
}

const faHighchartsLocale = resolveHighchartsLocale()
Highcharts.setOptions({ lang: { locale: faHighchartsLocale } })

const FA_LINE_META = {
	L1: { key: 'line1', title: 'Overall', subtitle: 'Line 1' },
	L2: { key: 'line2', title: 'Overall', subtitle: 'Line 2' },
}

function parseFaEquationSegments(name) {
	return String(name || '')
		.trim()
		.split('-')
		.map(function (part) {
			return part.trim()
		})
		.filter(Boolean)
}

function parseFaEquationNumber(value) {
	if (value === null || value === undefined || value === '') {
		return null
	}
	const num = Number(String(value).trim())
	return Number.isFinite(num) ? num : null
}

function buildFaEquationValueMap(equations) {
	const map = {}
	const rank = {}
	;(Array.isArray(equations) ? equations : []).forEach(function (row) {
		const name = String(row && row.equation_name ? row.equation_name : '').trim()
		if (!name) {
			return
		}
		const num = parseFaEquationNumber(row.calculated_value)
		if (num === null) {
			return
		}
		const statId = Number(row.fa_stat_id) || 0
		const dateKey = String(row.equation_date || '')
		const prev = rank[name]
		const better = !prev
			|| dateKey > prev.dateKey
			|| (dateKey === prev.dateKey && statId > prev.statId)
		if (better) {
			rank[name] = { dateKey: dateKey, statId: statId }
			map[name] = num
		}
	})
	return map
}

function sumFaNodeLeafValues(nodes) {
	let sum = 0
	let hasValue = false
	;(Array.isArray(nodes) ? nodes : []).forEach(function (node) {
		const children = Array.isArray(node.children) ? node.children : []
		if (children.length) {
			const childSum = sumFaNodeLeafValues(children)
			if (childSum != null) {
				sum += childSum
				hasValue = true
			}
			return
		}
		const num = parseFaEquationNumber(node.calculated_value)
		if (num !== null) {
			sum += num
			hasValue = true
		}
	})
	return hasValue ? sum : null
}

function buildFaHierarchyTree(equations) {
	const valueMap = buildFaEquationValueMap(equations)
	const names = []
	const seen = {}
	;(Array.isArray(equations) ? equations : []).forEach(function (row) {
		const name = String(row && row.equation_name ? row.equation_name : '').trim()
		if (!name || seen[name]) {
			return
		}
		seen[name] = true
		names.push(name)
	})

	const lineMap = {}

	names.forEach(function (name) {
		const segments = parseFaEquationSegments(name)
		if (segments.length < 2) {
			return
		}

		const lineCode = segments[0].toUpperCase()
		if (lineCode !== 'L1' && lineCode !== 'L2') {
			return
		}

		const branchLabel = segments[0] + '-' + segments[1]
		if (!lineMap[lineCode]) {
			lineMap[lineCode] = {
				meta: FA_LINE_META[lineCode],
				branches: {},
			}
		}

		const branchStore = lineMap[lineCode].branches
		if (!branchStore[branchLabel]) {
			branchStore[branchLabel] = { children: {} }
		}

		if (segments.length === 2) {
			return
		}

		let current = branchStore[branchLabel]
		for (let i = 2; i < segments.length; i++) {
			const pathLabel = segments.slice(0, i + 1).join('-')
			if (!current.children[pathLabel]) {
				current.children[pathLabel] = {
					label: pathLabel,
					children: {},
				}
			}
			current = current.children[pathLabel]
		}
	})

	function childrenToArray(node) {
		return Object.keys(node.children)
			.sort(function (a, b) {
				return String(a).localeCompare(String(b))
			})
			.map(function (key) {
				const entry = node.children[key]
				const children = childrenToArray(entry)
				const value = Object.prototype.hasOwnProperty.call(valueMap, entry.label)
					? valueMap[entry.label]
					: null
				return {
					label: entry.label,
					children,
					isLeaf: children.length === 0,
					calculated_value: value,
				}
			})
	}

	const lineOrder = ['L1', 'L2']
	return lineOrder
		.filter(function (code) {
			return !!lineMap[code]
		})
		.map(function (code) {
			const line = lineMap[code]
			const branchLabels = Object.keys(line.branches).sort(function (a, b) {
				return a.localeCompare(b)
			})
			const branches = branchLabels.map(function (branchLabel) {
				const children = childrenToArray(line.branches[branchLabel])
				return {
					key: branchLabel.toLowerCase(),
					label: branchLabel,
					calculated_value: sumFaNodeLeafValues(children),
					children,
				}
			}).filter(function (branch) {
				return branch.children.length > 0
			})

			return {
				key: line.meta.key,
				title: line.meta.title,
				subtitle: line.meta.subtitle,
				lineCode: code,
				calculated_value: sumFaNodeLeafValues(branches),
				branches,
			}
		})
		.filter(function (line) {
			return line.branches.length > 0
		})
}

export default {
	name: 'FalseAir',
	components: { Chart, Search, Pagination, FaOrgEquationNode },
	data() {
		const now = new Date()
		
		const y = now.getFullYear()
		const m = now.getMonth()
		const d = now.getDate()
		const pad = (n) => String(n).padStart(2, '0')
		const today = `${y}-${pad(m + 1)}-${pad(d)}`
		const mtdFrom = `${y}-${pad(m + 1)}-01`
		return {
			Highcharts,
			errors: {},
			frequencies: [],
			filters: {
				from_date: mtdFrom,
				to_date: today,
				frequency_id: '',
			},
			
			faCounts: {
				open: 0,
				closed: 0,
				total: 0,
			},
			faDeptDeviations: [],
			
			faListActive: null,
			faPipelineRows: [],
			
			faPipelineGroups: [],
			faPipelineLoading: false,
			
			faMonthTrend: null,
			faMonthTrendLoading: false,
			
			faEquationRows: [],
			faAllCalculationEquations: [],
			recalculatingFaStatId: null,
			recalculatingAll: false,
			faEquationMeta: {
				per_page: 5,
				currentPage: 1,
				lastPage: 1,
				from: 1,
				maxPage: 1,
			},
			overallFanPowerLossEquationName: 'L2-OverallFanPowerLoss-UnitsPerDay',
			overallFanPowerLossValue: null,
			
			faPipelineStageHeadings: [
				'Initialized',
				'Assign',
				'Fixed',
				'Re-inspection',
			],
			faStageTooltip: null,
			faDetailModal: null,
			faDetailModalInstance: null,
			faEquationChartModal: null,
			faEquationChartModalInstance: null,
			faEquationChartInstance: null,
			faEquationChartLoading: false,
			faEquationChartReady: false,
			faHighchartsLocale,
		}
	},
	beforeUnmount() {
		this.destroyFaEquationChart()
		if (this.faEquationChartModalInstance) {
			this.faEquationChartModalInstance.dispose()
			this.faEquationChartModalInstance = null
		}
	},
	computed: {
		pipelineSectionTitle() {
			if (this.faListActive === 'open') {
				return 'Open (not submitted & in progress)'
			}
			return 'Closed (completed)'
		},
		pipelineListEmpty() {
			const groups = this.faPipelineGroups || []
			const rows = this.faPipelineRows || []
			if (this.faListActive === 'closed') {
				return !groups.length
			}
			if (groups.length) {
				return false
			}
			return !rows.length
		},
		
		openJobCount() {
			return this.faCounts.open
		},
		
		closedJobCount() {
			return this.faCounts.closed
		},
		totalJobs() {
			const t = Number(this.faCounts.total)
			if (!isNaN(t) && t > 0) {
				return t
			}
			return this.faCounts.open + this.faCounts.closed
		},
		faPlantCode() {
			const user = this.$store.getters.user
			if (user && user.plant && user.plant.plant_code) {
				return String(user.plant.plant_code)
			}
			if (user && user.plant_code) {
				return String(user.plant_code)
			}
			return '—'
		},
		faHierarchyTree() {
			// Hierarchy values come only from the full latest-equations payload
			const rows = Array.isArray(this.faAllCalculationEquations)
				? this.faAllCalculationEquations
				: []
			return buildFaHierarchyTree(rows)
		},
		faHierarchyRootValue() {
			const lines = this.faHierarchyTree || []
			let sum = 0
			let hasValue = false
			for (let i = 0; i < lines.length; i++) {
				const num = this.parseCalculatedValue(lines[i].calculated_value)
				if (num !== null) {
					sum += num
					hasValue = true
				}
			}
			return hasValue ? sum : null
		},
		pieChartOptions() {
			const rows = Array.isArray(this.faDeptDeviations) ? this.faDeptDeviations : []
			const data = rows
				.map((r) => {
					const name = String(r.department_name || 'Unknown')
					const y = Number(r.deviated_count) || 0
					return { name, y }
				})
				.filter((p) => p.y > 0)
			return {
				chart: { type: 'pie', height: 280 },
				title: { text: null },
				tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)' },
				plotOptions: {
					pie: {
						allowPointSelect: true,
						cursor: 'pointer',
						dataLabels: {
							enabled: true,
							format: '{point.name}: {point.y}',
						},
						showInLegend: true,
					},
				},
				series: [{
					name: 'Deviations',
					colorByPoint: true,
					data,
				}],
				credits: { enabled: false },
			}
		},
		faMonthStripTitle() {
			const t = this.faMonthTrend
			if (!t || !t.year || !t.month) {
				return 'Inspections by day'
			}
			const names = [
				'January', 'February', 'March', 'April', 'May', 'June',
				'July', 'August', 'September', 'October', 'November', 'December',
			]
			const mi = Number(t.month) - 1
			if (mi < 0 || mi > 11) {
				return 'Inspections by day'
			}
			return names[mi] + ' ' + String(t.year)
		},
		faMonthTotalTemplates() {
			const t = this.faMonthTrend
			if (!t || t.total_templates == null) {
				return 0
			}
			const n = Number(t.total_templates)
			if (isNaN(n)) {
				return 0
			}
			return n
		},
		faMonthStripDays() {
			const t = this.faMonthTrend
			if (!t || !Array.isArray(t.days)) {
				return []
			}
			return t.days
		},
		
		faMonthStripDaysUptoToday() {
			const days = this.faMonthStripDays
			if (!days.length) {
				return []
			}
			const t = this.faMonthTrend
			if (!t || t.year == null || t.month == null) {
				return []
			}
			const ty = Number(t.year)
			const tm = Number(t.month)
			const now = new Date()
			const ny = now.getFullYear()
			const nm = now.getMonth() + 1
			const nd = now.getDate()
			if (ty < ny || (ty === ny && tm < nm) || ty > ny || (ty === ny && tm > nm)) {
				return days
			}
			const out = []
			for (let i = 0; i < days.length; i++) {
				const day = days[i]
				const dn = Number(day.day)
				if (!isNaN(dn) && dn <= nd) {
					out.push(day)
				}
			}
			return out
		},
	},
	mounted() {
		const vm = this
		vm.loadFaFilterOptions().then(function () {
			return vm.loadDashboard()
		})
	},
	activated() {
		const vm = this
		vm.loadFaFilterOptions().then(function () {
			return vm.loadDashboard()
		})
	},
	methods: {
		loadFaFilterOptions() {
			const vm = this
			const user = vm.$store.getters.user
			let pid = null
			if (user && user.plant_id != null && user.plant_id !== '') {
				pid = user.plant_id
			}
			let plantPayload = {}
			if (pid) {
				plantPayload = { plant_id: pid }
			}
			let freqUri = 'getFrequencies'
			if (pid) {
				freqUri = 'getFrequenciesPlantWise'
			}
			return vm.$store
				.dispatch('post', { uri: freqUri, data: plantPayload })
				.then(function (res) {
					let rows = []
					if (res.data && res.data.data) {
						rows = res.data.data
					}
					vm.frequencies = rows
					vm.maybeDefaultFrequency()
					vm.applyFaDateRangeForSelectedFrequency()
				})
				.catch(function () {
					vm.frequencies = []
				})
		},
		
		maybeDefaultFrequency() {
			const f = this.filters
			if (f.frequency_id !== '' && f.frequency_id != null) {
				return
			}
			const list = this.frequencies || []
			if (!list.length) {
				return
			}
			let monthlyRow = null
			for (let i = 0; i < list.length; i++) {
				const row = list[i]
				const name = String(row.frequency_name || '')
					.trim()
					.toLowerCase()
				if (name === 'monthly') {
					monthlyRow = row
					break
				}
			}
			if (monthlyRow) {
				f.frequency_id = monthlyRow.frequency_id
			} else {
				f.frequency_id = list[0].frequency_id
			}
		},
		onFaFrequencyInput(value) {
			this.filters.frequency_id = value
			this.applyFaDateRangeForSelectedFrequency()
		},
		
		faFrequencyNameLike(frequency_name, label) {
			const fn = String(frequency_name || '')
			if (fn.includes(label)) {
				return true
			}
			const low = fn.toLowerCase()
			const lab = String(label || '').toLowerCase()

			return low.includes(lab)
		},
		applyFaDateRangeForSelectedFrequency() {
			const fid = this.filters.frequency_id
			if (fid === '' || fid == null) {
				return
			}
			const rows = this.frequencies || []
			let row = null
			for (let i = 0; i < rows.length; i++) {
				if (rows[i].frequency_id == fid) {
					row = rows[i]
					break
				}
			}
			if (!row) {
				return
			}
			const frequency_name = String(row.frequency_name || '')

			if (this.faFrequencyNameLike(frequency_name, 'Daily')) {
				const d = moment().subtract(1, 'days')
				this.filters.from_date = d.format('YYYY-MM-DD')
				this.filters.to_date = d.format('YYYY-MM-DD')
			} else if (this.faFrequencyNameLike(frequency_name, 'Weekly')) {
				const w = moment().subtract(1, 'weeks')
				this.filters.from_date = w.clone().startOf('isoWeek').format('YYYY-MM-DD')
				this.filters.to_date = w.clone().endOf('isoWeek').format('YYYY-MM-DD')
			} else if (this.faFrequencyNameLike(frequency_name, 'Fortnightly')) {
				const today = moment()
				if (today.date() <= 15) {
					this.filters.from_date = today.clone().startOf('month').format('YYYY-MM-DD')
					this.filters.to_date = today.format('YYYY-MM-DD')
				} else {
					this.filters.from_date = today.clone().date(16).format('YYYY-MM-DD')
					this.filters.to_date = today.format('YYYY-MM-DD')
				}
			} else if (this.faFrequencyNameLike(frequency_name, 'Monthly')) {
				this.filters.from_date = moment().startOf('month').format('YYYY-MM-DD')
				this.filters.to_date = moment().format('YYYY-MM-DD')
			} else {
				this.filters.from_date = moment().startOf('month').format('YYYY-MM-DD')
				this.filters.to_date = moment().subtract(1, 'days').format('YYYY-MM-DD')
			}
		},
		appendFaFilterParams(data) {
			const f = this.filters
			if (f.frequency_id !== '' && f.frequency_id != null) {
				data.frequency_id = f.frequency_id
			}
		},
		loadDashboard() {
			const vm = this
			vm.errors = {}
			const from = (vm.filters.from_date || '').trim()
			const to = (vm.filters.to_date || '').trim()
			if (!from || !to) {
				vm.$store.dispatch('error', 'Please select both From date and To date.')
				return Promise.reject(new Error('missing_dates'))
			}
			if (from > to) {
				vm.$store.dispatch('error', 'From date must be on or before To date.')
				return Promise.reject(new Error('bad_range'))
			}
			const user = vm.$store.getters.user
			if (!user || !user.token) {
				vm.$store.dispatch('error', 'Please sign in to load the dashboard.')
				return Promise.reject(new Error('no_user'))
			}
			const data = { from_date: from, to_date: to }
			if (user.plant_id != null && user.plant_id !== '') {
				data.plant_id = user.plant_id
			}
			vm.appendFaFilterParams(data)
			return vm.$store
				.dispatch('post', { uri: 'faDashboardCounts', data: data })
				.then(function (response) {
					const body = response.data || {}
					const openC = Number(body.open) || 0
					const closedC = Number(body.closed) || 0
					let totalC = Number(body.total)
					if (isNaN(totalC) || totalC < 1) {
						totalC = openC + closedC
					}
					vm.faCounts = {
						open: openC,
						closed: closedC,
						total: totalC,
					}
					if (vm.faListActive) {
						vm.loadPipelineRows(vm.faListActive)
					}
				})
				.then(function () {
					// Only show Department-wise deviations when "Open" view is active
					if (vm.faListActive === 'open') {
						return vm.loadFaDeptDeviations(data)
					}
					vm.faDeptDeviations = []
					return Promise.resolve()
				})
				.then(function () {
					return vm.loadFaMonthTrend()
				})
				.then(function () {
					return vm.loadFaEquations()
				})
				.then(function () {
					return vm.loadFaAllCalculationEquations()
				})
				.then(function () {
					return vm.loadOverallFanPowerLossValue()
				})
				.catch(function (error) {
					const d = error.response && error.response.data
					if (d && d.errors && typeof d.errors === 'object') {
						vm.errors = d.errors
					}
					let msg = (d && d.message) || 'Failed to load False Air counts.'
					if (d && d.errors && typeof d.errors === 'object') {
						const first = Object.keys(d.errors)[0]
						if (first && d.errors[first] && d.errors[first][0]) {
							msg = d.errors[first][0]
						}
					}
					vm.$store.dispatch('error', msg)
					return Promise.reject(error)
				})
		},
		parseCalculatedValue(value) {
			if (value === null || value === undefined || value === '') {
				return null
			}
			const trimmed = String(value).trim()
			if (trimmed === '' || trimmed === '-') {
				return null
			}
			const num = Number(trimmed)
			return Number.isFinite(num) ? num : null
		},

		formatCalculatedValue(value) {
			const num = this.parseCalculatedValue(value)
			if (num === null) {
				return '-'
			}
			return num.toFixed(3)
		},

		calculatedValueClass(value) {
			const num = this.parseCalculatedValue(value)
			if (num === null) {
				return ''
			}
			if (num <= 5) {
				return 'fa-calc-value-green'
			}
			if (num <= 10) {
				return 'fa-calc-value-yellow'
			}
			return 'fa-calc-value-red'
		},

		loadFaEquations() {
			const vm = this
			const user = vm.$store.getters.user
			const plantId = user && user.plant_id
			let uri = 'faDashboardEquations?page=' + vm.faEquationMeta.currentPage
				+ '&per_page=' + vm.faEquationMeta.per_page
			if (plantId) {
				uri += '&plant_id=' + plantId
			}
			return vm.$store
				.dispatch('post', { uri })
				.then(function (response) {
					const body = response.data || {}
					vm.faEquationRows = Array.isArray(body.data) ? body.data : []
					const meta = body.meta || body
					const last = Number(meta.last_page || 1) || 1
					const from = Number(meta.from || 1) || 1
					const current = Number(meta.current_page || vm.faEquationMeta.currentPage) || 1
					vm.faEquationMeta.lastPage = last
					vm.faEquationMeta.from = from
					vm.faEquationMeta.currentPage = current
					vm.faEquationMeta.maxPage = vm.faEquationMeta.lastPage >= 3 ? 3 : vm.faEquationMeta.lastPage
				})
				.catch(function (error) {
					vm.faEquationRows = []
					const d = error.response && error.response.data
					const message = (d && d.message) || 'Could not load calculations.'
					vm.$store.dispatch('error', message)
				})
		},

		loadFaAllCalculationEquations() {
			const vm = this
			const user = vm.$store.getters.user
			const plantId = user && user.plant_id
			let uri = 'faDashboardEquations?page=1&per_page=500'
			if (plantId) {
				uri += '&plant_id=' + plantId
			}
			return vm.$store
				.dispatch('post', { uri })
				.then(function (response) {
					const body = response.data || {}
					vm.faAllCalculationEquations = Array.isArray(body.data) ? body.data : []
				})
				.catch(function () {
					vm.faAllCalculationEquations = []
				})
		},

		loadOverallFanPowerLossValue() {
			const vm = this
			const user = vm.$store.getters.user
			const data = {}
			if (user && user.plant_id != null && user.plant_id !== '') {
				data.plant_id = user.plant_id
			}
			return vm.$store
				.dispatch('post', {
					uri: 'getFAEquationLatestValue',
					data,
				})
				.then(function (response) {
					const body = response.data && response.data.data ? response.data.data : {}
					vm.overallFanPowerLossValue = body.calculated_value != null ? body.calculated_value : null
				})
				.catch(function () {
					vm.overallFanPowerLossValue = null
				})
		},

		recalculateFaEquation(row) {
			const vm = this
			if (!row || !row.fa_stat_id) {
				return
			}
			vm.recalculatingFaStatId = row.fa_stat_id
			return vm.$store
				.dispatch('post', {
					uri: 'recalculateFAEquation',
					data: { fa_stat_id: row.fa_stat_id },
				})
				.then(function (response) {
					const body = response.data && response.data.data ? response.data.data : {}
					const value = body.calculated_value != null ? body.calculated_value : ''
					const formatted = value !== '' ? vm.formatCalculatedValue(value) : ''
					vm.$store.dispatch('success', formatted !== '' && formatted !== '-' ? `Recalculated: ${formatted}` : 'Recalculated successfully.')
					return vm.loadFaEquations().then(function () {
						return vm.loadFaAllCalculationEquations()
					}).then(function () {
						return vm.loadOverallFanPowerLossValue()
					})
				})
				.catch(function (error) {
					const d = error.response && error.response.data
					let message = (d && d.message) || 'Could not recalculate equation.'
					if (d && d.errors && typeof d.errors === 'object') {
						const first = Object.keys(d.errors)[0]
						if (first && d.errors[first] && d.errors[first][0]) {
							message = d.errors[first][0]
						}
					}
					vm.$store.dispatch('error', message)
				})
				.finally(function () {
					vm.recalculatingFaStatId = null
				})
		},

		recalculateAllFaEquations() {
			const vm = this
			const rows = vm.faEquationRows || []
			if (!rows.length) {
				vm.$store.dispatch('error', 'No equations to recalculate.')
				return Promise.resolve()
			}
			vm.recalculatingAll = true
			let succeeded = 0
			let failed = 0
			const chain = rows.reduce(function (promise, row) {
				return promise.then(function () {
					if (!row || !row.fa_stat_id) {
						return Promise.resolve()
					}
					return vm.$store
						.dispatch('post', {
							uri: 'recalculateFAEquation',
							data: { fa_stat_id: row.fa_stat_id },
						})
						.then(function () {
							succeeded++
						})
						.catch(function () {
							failed++
						})
				})
			}, Promise.resolve())
			return chain
				.then(function () {
					return vm.loadFaEquations()
				})
				.then(function () {
					return vm.loadFaAllCalculationEquations()
				})
				.then(function () {
					return vm.loadOverallFanPowerLossValue()
				})
				.then(function () {
					if (failed === 0) {
						vm.$store.dispatch('success', 'Recalculated ' + succeeded + ' equation(s).')
					} else if (succeeded === 0) {
						vm.$store.dispatch('error', 'Could not recalculate equations.')
					} else {
						vm.$store.dispatch('error', 'Recalculated ' + succeeded + ', failed ' + failed + '.')
					}
				})
				.finally(function () {
					vm.recalculatingAll = false
				})
		},
		
		onFaEquationPageChange(page) {
			this.faEquationMeta.currentPage = page
			this.loadFaEquations()
		},
		loadFaMonthTrend() {
			const vm = this
			const raw = (vm.filters.from_date || '').trim()
			const parts = raw.split('-')
			if (parts.length < 2) {
				vm.faMonthTrend = null
				return Promise.resolve()
			}
			const year = parseInt(parts[0], 10)
			const month = parseInt(parts[1], 10)
			if (isNaN(year) || isNaN(month) || month < 1 || month > 12) {
				vm.faMonthTrend = null
				return Promise.resolve()
			}
			const user = vm.$store.getters.user
			if (!user || !user.token) {
				return Promise.resolve()
			}
			const data = { year: year, month: month }
			if (user.plant_id != null && user.plant_id !== '') {
				data.plant_id = user.plant_id
			}
			vm.appendFaFilterParams(data)
			vm.faMonthTrendLoading = true
			return vm.$store
				.dispatch('post', { uri: 'faDashboardMonthDays', data: data })
				.then(function (res) {
					vm.faMonthTrend = res.data || null
				})
				.catch(function (error) {
					vm.faMonthTrend = null
					const d = error.response && error.response.data
					let msg = (d && d.message) || 'Failed to load month day trend.'
					if (d && d.errors && typeof d.errors === 'object') {
						const first = Object.keys(d.errors)[0]
						if (first && d.errors[first] && d.errors[first][0]) {
							msg = d.errors[first][0]
						}
					}
					vm.$store.dispatch('error', msg)
				})
				.finally(function () {
					vm.faMonthTrendLoading = false
				})
		},
		faTodayYmd() {
			const n = new Date()
			const pad = (x) => String(x).padStart(2, '0')
			return String(n.getFullYear()) + '-' + pad(n.getMonth() + 1) + '-' + pad(n.getDate())
		},
		faSelectedFrequencyKind() {
			const fid = this.filters.frequency_id
			if (fid === '' || fid == null) {
				return ''
			}
			const rows = this.frequencies || []
			let row = null
			for (let i = 0; i < rows.length; i++) {
				if (rows[i].frequency_id == fid) {
					row = rows[i]
					break
				}
			}
			if (!row) {
				return ''
			}
			const fn = String(row.frequency_name || '')
			if (this.faFrequencyNameLike(fn, 'Monthly')) {
				return 'monthly'
			}
			if (this.faFrequencyNameLike(fn, 'Fortnightly')) {
				return 'fortnightly'
			}
			if (this.faFrequencyNameLike(fn, 'Weekly')) {
				return 'weekly'
			}
			if (this.faFrequencyNameLike(fn, 'Daily')) {
				return 'daily'
			}
			return 'other'
		},
		faIsMonthStripDayClickable(day) {
			let d = ''
			if (day && day.date) {
				d = String(day.date)
			}
			if (!d) {
				return false
			}
			const mode = this.faSelectedFrequencyKind()
			if (mode === 'monthly' || mode === 'fortnightly') {
				return d <= this.faTodayYmd()
			}
			return this.isFaLocalToday(d)
		},
		isFaMonthStripDaySelected(dayDate) {
			if (this.faListActive !== 'open') {
				return false
			}
			const to = (this.filters.to_date || '').trim()
			const mode = this.faSelectedFrequencyKind()
			if (mode === 'monthly' || mode === 'fortnightly') {
				return to === dayDate
			}
			const from = (this.filters.from_date || '').trim()
			return from === dayDate && to === dayDate
		},
		onFaMonthStripDayClick(day) {
			const vm = this
			let clickDate = ''
			if (day && day.date) {
				clickDate = String(day.date)
			}
			if (!clickDate || !vm.faIsMonthStripDayClickable(day)) {
				return
			}
			const mode = vm.faSelectedFrequencyKind()
			if (mode === 'monthly') {
				const m = moment(clickDate, 'YYYY-MM-DD')
				vm.filters.from_date = m.clone().startOf('month').format('YYYY-MM-DD')
				vm.filters.to_date = clickDate
			} else if (mode === 'fortnightly') {
				const m = moment(clickDate, 'YYYY-MM-DD')
				const dom = m.date()
				if (dom <= 15) {
					vm.filters.from_date = m.clone().startOf('month').format('YYYY-MM-DD')
				} else {
					vm.filters.from_date = m.clone().date(16).format('YYYY-MM-DD')
				}
				vm.filters.to_date = clickDate
			} else if (!vm.isFaLocalToday(clickDate)) {
				return
			}
			vm.loadDashboard().then(function () {
				vm.selectFaList('open')
			})
		},
		faMonthStripDayTitle(day) {
			const base = this.faMonthDayTooltip(day)
			const mode = this.faSelectedFrequencyKind()
			if (mode === 'monthly' || mode === 'fortnightly') {
				return base + ' Click to set From/To through this day and reload the Open pipeline.'
			}
			return base + ' Click to open the Open pipeline (today).'
		},
		isFaLocalToday(dateStr) {
			const n = new Date()
			const pad = function (x) {
				return String(x).padStart(2, '0')
			}
			const t = String(n.getFullYear()) + '-' + pad(n.getMonth() + 1) + '-' + pad(n.getDate())
			return dateStr === t
		},
		faMonthDayTooltip(day) {
			return (
				day.date +
				': ' +
				day.remaining_open +
				' template(s) not fully closed (FA) through this day, ' +
				day.deviated_count +
				' deviated component(s) that day.'
			)
		},
		faWeekdayShort(dateStr) {
			const pr = dateStr.split('-')
			if (pr.length < 3) {
				return ''
			}
			const y = parseInt(pr[0], 10)
			const mo = parseInt(pr[1], 10) - 1
			const da = parseInt(pr[2], 10)
			const dt = new Date(y, mo, da, 12, 0, 0)
			const w = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
			return w[dt.getDay()]
		},
		selectFaList(status) {
			this.faListActive = status
			this.loadPipelineRows(status)
			if (status === 'open') {
				const from = (this.filters.from_date || '').trim()
				const to = (this.filters.to_date || '').trim()
				const user = this.$store.getters.user
				const data = { from_date: from, to_date: to }
				if (user && user.plant_id != null && user.plant_id !== '') {
					data.plant_id = user.plant_id
				}
				this.appendFaFilterParams(data)
				this.loadFaDeptDeviations(data)
			} else {
				this.faDeptDeviations = []
			}
		},
		clearFaList() {
			this.faListActive = null
			this.faPipelineRows = []
			this.faPipelineGroups = []
			this.faPipelineLoading = false
			this.faDeptDeviations = []
		},
		loadFaDeptDeviations(data) {
			const vm = this
			return vm.$store
				.dispatch('post', { uri: 'faDashboardDepartmentDeviations', data: data })
				.then(function (res) {
					const body = res.data || {}
					vm.faDeptDeviations = Array.isArray(body.departments) ? body.departments : []
				})
				.catch(function () {
					vm.faDeptDeviations = []
				})
		},
		loadPipelineRows(status) {
			const vm = this
			const from = (vm.filters.from_date || '').trim()
			const to = (vm.filters.to_date || '').trim()
			if (!from || !to || from > to) {
				vm.$store.dispatch('error', 'Set valid From and To dates, then Generate.')
				return
			}
			const user = vm.$store.getters.user
			if (!user || !user.token) {
				vm.$store.dispatch('error', 'Please sign in to load the dashboard.')
				return
			}
			const data = { from_date: from, to_date: to, status: status }
			if (user.plant_id != null && user.plant_id !== '') {
				data.plant_id = user.plant_id
			}
			if (status === 'open') {
				const n = new Date()
				const p = (x) => String(x).padStart(2, '0')
				data.pipeline_as_of_date = `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`
			}
			vm.appendFaFilterParams(data)
			vm.faPipelineLoading = true
			vm.faPipelineRows = []
			vm.faPipelineGroups = []
			vm.$store
				.dispatch('post', { uri: 'faDashboardRows', data: data })
				.then(function (response) {
					const body = response.data || {}
					if (status === 'closed' && Array.isArray(body.groups)) {
						vm.faPipelineGroups = body.groups
						vm.faPipelineRows = []
					} else if (status === 'open') {
						let rows = []
						if (Array.isArray(body.rows)) {
							rows = body.rows
						}
						vm.faPipelineRows = rows
						let og = []
						if (Array.isArray(body.groups)) {
							og = body.groups
						}
						vm.faPipelineGroups = og
					} else {
						let rows = []
						if (Array.isArray(body.rows)) {
							rows = body.rows
						}
						vm.faPipelineRows = rows
						vm.faPipelineGroups = []
					}
				})
				.catch(function (error) {
					const d = error.response && error.response.data
					let msg = (d && d.message) || 'Failed to load template rows.'
					if (d && d.errors && typeof d.errors === 'object') {
						const first = Object.keys(d.errors)[0]
						if (first && d.errors[first] && d.errors[first][0]) {
							msg = d.errors[first][0]
						}
					}
					vm.$store.dispatch('error', msg)
					vm.faPipelineRows = []
					vm.faPipelineGroups = []
				})
				.finally(function () {
					vm.faPipelineLoading = false
				})
		},
		stageStateLabel(state) {
			if (state === 'completed') return 'Completed'
			if (state === 'pending') return 'Pending'
			if (state === 'remaining') return 'Remaining'
			return state || '—'
		},
		stageSegmentTitle(st) {
			let t = st.label + ' (' + this.stageStateLabel(st.state) + '): ' + this.openStageDayCount(st) + 'd'
			if (st.completed_on) {
				t += ', done ' + st.completed_on
			}
			return t
		},
		faPipelineStageDeviationEquipment(st) {
			if (!st || st.deviated_equipment_names == null) {
				return ''
			}
			const s = String(st.deviated_equipment_names).trim()
			return s
		},
		closedPipelineJobClass() {
			return 'fa-pipeline-id-done'
		},
		openStageDayCount(st) {
			if (st && st.key === 'templates') {
				return 0
			}
			const d = Number(st.days)
			if (isNaN(d)) {
				return 0
			}
			return d
		},
		openRowTotalDays(row) {
			let maxDays = 0
			const stages = row.stages
			if (!stages || !stages.length) {
				if (row.total_delay_days != null) {
					return Number(row.total_delay_days) || 0
				}
				return 0
			}
			for (let i = 0; i < stages.length; i++) {
				const st = stages[i]
				if (st && st.key === 'templates') {
					continue
				}
				const d = Number(st.days)
				if (!isNaN(d) && d > maxDays) {
					maxDays = d
				}
			}
			if (maxDays === 0 && row.total_delay_days != null) {
				return Number(row.total_delay_days) || 0
			}
			return maxDays
		},
		faStageDisplayLabel(st, index) {
			const headings = this.faPipelineStageHeadings || []
			if (index >= 0 && index < headings.length) {
				return headings[index]
			}
			if (st && st.label) {
				return st.label
			}
			return '—'
		},
		buildStageTooltipLines(st) {
			const lines = []
			lines.push(`${this.openStageDayCount(st)}d`)
			lines.push(this.stageStateLabel(st.state))
			if (st.completed_on) {
				lines.push(st.completed_on)
			}
			const dc = Number(st.deviated_count) || 0
			if (st.completed_on && (st.key === 'templates' || st.key === 'reinspection') && dc > 0) {
				const dev = this.faPipelineStageDeviationEquipment(st)
				lines.push(`Dev: ${dc}${dev ? ` (${dev})` : ''}`)
			}
			return lines
		},
		onStageHover(event, st) {
			const idx = this.faStageIndexFromKey(st && st.key)
			this.faStageTooltip = {
				visible: true,
				x: event.clientX + 14,
				y: event.clientY + 14,
				heading: this.faStageDisplayLabel(st, idx),
				lines: this.buildStageTooltipLines(st),
			}
		},
		moveStageTooltip(event) {
			if (!this.faStageTooltip) {
				return
			}
			this.faStageTooltip.x = event.clientX + 14
			this.faStageTooltip.y = event.clientY + 14
		},
		hideStageTooltip() {
			this.faStageTooltip = null
		},
		faStageIndexFromKey(key) {
			const order = ['templates', 'assign', 'fixing', 'reinspection']
			const ix = order.indexOf(String(key || ''))
			return ix >= 0 ? ix : 0
		},
		formatFaEquationChartDate(value) {
			if (!value) {
				return '—'
			}
			const parsed = moment(value)
			if (!parsed.isValid()) {
				return String(value)
			}
			return parsed.format('DD-MMM-YYYY')
		},

		formatFaChartDataLabel(value) {
			const num = this.parseCalculatedValue(value)
			if (num === null) {
				return ''
			}
			return num.toFixed(3)
		},

		canShowFaEquationChart() {
			return !this.faEquationChartLoading
				&& this.faEquationChartReady
				&& this.faEquationChartModal
				&& Array.isArray(this.faEquationChartModal.points)
				&& this.faEquationChartModal.points.length > 0
		},

		buildFaEquationChartOptions(chartWidth) {
			const modal = this.faEquationChartModal
			const points = modal && Array.isArray(modal.points) ? modal.points : []
			const formatLabel = (value) => this.formatFaChartDataLabel(value)
			const chartPoints = points
				.map((point) => ({
					category: this.formatFaEquationChartDate(point.equation_date),
					value: this.parseCalculatedValue(point.calculated_value),
				}))
				.filter((point) => point.value != null && !isNaN(point.value))
			const categories = chartPoints.map((point) => point.category)
			const data = chartPoints.map((point) => point.value)
			const validValues = data.filter((v) => v != null && !isNaN(v))
			const maxVal = validValues.length ? Math.max(...validValues) : 0
			const yMax = maxVal > 0 ? Math.ceil(maxVal * 1.15 / 5) * 5 : 5
			const tickInterval = yMax <= 10 ? 1 : 5

			return {
				chart: {
					type: 'line',
					width: chartWidth,
					height: 420,
					spacingTop: 16,
					spacingBottom: 16,
					spacingLeft: 10,
					spacingRight: 16,
					marginLeft: 70,
					marginRight: 30,
					marginBottom: 95,
					marginTop: 70,
				},
				title: {
					text: modal ? modal.equation_name : '',
					style: {
						fontSize: '16px',
						fontWeight: '700',
						color: '#111827',
					},
				},
				xAxis: {
					categories,
					title: { text: null },
					labels: {
						rotation: -45,
						align: 'right',
						style: { fontSize: '11px', color: '#334155' },
						y: 16,
					},
					tickLength: 0,
					lineColor: '#cbd5e1',
					gridLineWidth: 1,
					gridLineColor: '#e2e8f0',
				},
				yAxis: {
					min: 0,
					max: yMax,
					tickInterval,
					title: { text: null },
					gridLineColor: '#e2e8f0',
					labels: {
						formatter: function () {
							return String(this.value)
						},
						style: { fontSize: '12px', color: '#334155' },
					},
				},
				tooltip: {
					headerFormat: '<span style="font-size:11px">{point.key}</span><br/>',
					pointFormat: '<b>{point.y}</b>',
				},
				plotOptions: {
					line: {
						marker: {
							enabled: true,
							radius: 4,
							symbol: 'circle',
							lineWidth: 1,
							lineColor: '#ffffff',
						},
						lineWidth: 2,
						dataLabels: {
							enabled: true,
							y: -10,
							formatter: function () {
								return formatLabel(this.y)
							},
							style: {
								fontSize: '11px',
								fontWeight: '600',
								color: '#111827',
								textOutline: 'none',
							},
						},
					},
					series: {
						states: {
							hover: {
								lineWidthPlus: 0,
							},
						},
					},
				},
				legend: { enabled: false },
				series: [{
					name: 'Calculated Value',
					color: '#4572A7',
					data,
				}],
				credits: { enabled: false },
			}
		},

		destroyFaEquationChart() {
			if (this.faEquationChartInstance) {
				this.faEquationChartInstance.destroy()
				this.faEquationChartInstance = null
			}
			const container = this.$refs.faEquationChartContainer
			if (container) {
				container.innerHTML = ''
			}
		},

		renderFaEquationChart(retryCount = 0) {
			const vm = this
			if (!vm.canShowFaEquationChart()) {
				vm.destroyFaEquationChart()
				return
			}
			const container = vm.$refs.faEquationChartContainer
			if (!container) {
				if (retryCount < 6) {
					window.setTimeout(function () {
						vm.renderFaEquationChart(retryCount + 1)
					}, 120)
				}
				return
			}
			const width = container.offsetWidth || container.clientWidth
			if (width < 240) {
				if (retryCount < 10) {
					window.setTimeout(function () {
						vm.renderFaEquationChart(retryCount + 1)
					}, 120)
				}
				return
			}
			vm.destroyFaEquationChart()
			vm.faEquationChartInstance = Highcharts.chart(container, vm.buildFaEquationChartOptions(width))
		},

		maybeRenderFaEquationChart() {
			const vm = this
			if (!vm.canShowFaEquationChart()) {
				return
			}
			vm.$nextTick(function () {
				window.setTimeout(function () {
					vm.renderFaEquationChart(0)
				}, 180)
			})
		},

		openFaEquationChartModal(row) {
			const vm = this
			if (!row || !row.equation_name) {
				return
			}
			vm.faEquationChartReady = false
			vm.faEquationChartModal = {
				equation_name: row.equation_name,
				points: [],
			}
			vm.faEquationChartLoading = true
			vm.$nextTick(function () {
				const el = document.getElementById('faEquationChartModal')
				if (!el) {
					return
				}
				if (vm.faEquationChartModalInstance) {
					vm.faEquationChartModalInstance.dispose()
				}
				vm._faEquationChartShownHandler = function () {
					vm.faEquationChartReady = true
					vm.maybeRenderFaEquationChart()
				}
				el.removeEventListener('shown.bs.modal', vm._faEquationChartShownHandler)
				el.addEventListener('shown.bs.modal', vm._faEquationChartShownHandler)
				vm.faEquationChartModalInstance = new Modal(el)
				vm.faEquationChartModalInstance.show()
			})
			vm.loadFaEquationChartHistory(row.equation_name)
		},

		loadFaEquationChartHistory(equationName) {
			const vm = this
			const user = vm.$store.getters.user
			const data = {
				equation_name: equationName,
				limit: 10,
			}
			if (user && user.plant_id != null && user.plant_id !== '') {
				data.plant_id = user.plant_id
			}
			return vm.$store
				.dispatch('post', {
					uri: 'getFAEquationHistory',
					data,
				})
				.then(function (response) {
					const body = response.data && response.data.data ? response.data.data : {}
					if (vm.faEquationChartModal && vm.faEquationChartModal.equation_name === equationName) {
						vm.faEquationChartModal = {
							equation_name: body.equation_name || equationName,
							points: Array.isArray(body.points) ? body.points : [],
						}
					}
				})
				.catch(function (error) {
					if (vm.faEquationChartModal && vm.faEquationChartModal.equation_name === equationName) {
						vm.faEquationChartModal.points = []
					}
					const d = error.response && error.response.data
					const message = (d && d.message) || 'Could not load equation history.'
					vm.$store.dispatch('error', message)
				})
				.finally(function () {
					vm.faEquationChartLoading = false
					vm.maybeRenderFaEquationChart()
				})
		},

		closeFaEquationChartModal() {
			const el = document.getElementById('faEquationChartModal')
			if (el && this._faEquationChartShownHandler) {
				el.removeEventListener('shown.bs.modal', this._faEquationChartShownHandler)
			}
			this.destroyFaEquationChart()
			if (this.faEquationChartModalInstance) {
				this.faEquationChartModalInstance.hide()
			}
			this.faEquationChartModal = null
			this.faEquationChartLoading = false
			this.faEquationChartReady = false
		},

		openFaDetailModal(payload) {
			this.faDetailModal = payload
			this.$nextTick(() => {
				const el = document.getElementById('faDetailModal')
				if (!el) {
					return
				}
				if (this.faDetailModalInstance) {
					this.faDetailModalInstance.dispose()
				}
				this.faDetailModalInstance = new Modal(el)
				this.faDetailModalInstance.show()
			})
		},
		openFaDetailModalFromLine(line, group) {
			this.openFaDetailModal({
				display_job_no: line.display_job_no,
				template_name: group.template_name,
				stages: line.stages || [],
				total_delay_days: line.total_delay_days != null ? line.total_delay_days : 0,
				is_rework: !!line.is_rework,
			})
		},
		openFaDetailModalFromRow(row) {
			this.openFaDetailModal({
				display_job_no: row.job_no,
				template_name: row.template_name,
				stages: row.stages || [],
				total_delay_days: this.openRowTotalDays(row),
				is_rework: false,
			})
		},
		closeFaDetailModal() {
			if (this.faDetailModalInstance) {
				this.faDetailModalInstance.hide()
			}
			this.faDetailModal = null
		},
	},
}
</script>

<style scoped>
.false-air-wrapper {
	width: 100%;
	min-height: 100%;
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.fa-main-card {
	background: #fff;
	border-radius: 16px;
	overflow: hidden;
	margin-bottom: 24px;
	box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
	border: 1px solid rgba(15, 23, 42, 0.06);
}

.fa-main-card-header {
	background: linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #334155 100%);
	padding: 12px 18px;
	color: #fff;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.fa-main-card-title {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 700;
	letter-spacing: -0.02em;
}

.fa-main-card-body {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 24px 28px;
}

.fa-summary-top {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
}

.fa-summary-fan-wrap {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	flex: 0 0 auto;
}

.fa-section-title-spacer {
	visibility: hidden;
	user-select: none;
}

@media (max-width: 768px) {
	.fa-main-card-body {
		padding: 20px 16px;
	}

	.fa-summary-top {
		flex-direction: column;
		align-items: stretch;
	}

	.fa-summary-fan-wrap {
		align-items: stretch;
	}
}

.fa-calculations-card {
	margin-top: 18px;
	margin-bottom: 18px;
	background: #fff;
	border: 1px solid rgba(15, 23, 42, 0.08);
	border-radius: 16px;
	box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
	overflow: hidden;
}

.fa-hierarchy-card {
	margin-top: 18px;
	margin-bottom: 0;
	background: #fff;
	border: 1px solid rgba(15, 23, 42, 0.08);
	border-radius: 16px;
	box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
	overflow: visible;
}

.fa-hierarchy-head {
	padding: 18px 22px;
	border-bottom: 1px solid rgba(15, 23, 42, 0.08);
	background: linear-gradient(180deg, rgba(248, 250, 252, 1) 0%, rgba(255, 255, 255, 1) 100%);
}

.fa-hierarchy-body {
	padding: 16px 14px 24px;
	overflow-x: auto;
	overflow-y: visible;
}

.fa-org-chart {
	display: flex;
	flex-direction: column;
	align-items: center;
	min-width: 640px;
	width: max-content;
	margin: 0 auto;
	padding: 0 24px 16px;
}

.fa-org-node {
	background: #1a5276;
	color: #fff;
	text-align: center;
	padding: 6px 12px;
	min-width: 88px;
	font-weight: 700;
	line-height: 1.25;
	box-sizing: border-box;
}

.fa-org-node-root {
	min-width: 96px;
	padding: 7px 14px;
}

.fa-org-node-line {
	font-size: 0.75rem;
	white-space: nowrap;
}

.fa-org-node-sub {
	font-size: 0.7rem;
	font-weight: 600;
}

.fa-org-node-value {
	margin-top: 3px;
	font-size: 0.75rem;
	font-weight: 700;
	line-height: 1.2;
}

.fa-org-node-value.fa-calc-value-green {
	color: #4ade80;
}

.fa-org-node-value.fa-calc-value-yellow {
	color: #facc15;
}

.fa-org-node-value.fa-calc-value-red {
	color: #f87171;
}

.fa-org-node-branch {
	position: relative;
	min-width: 78px;
	padding: 6px 10px;
	font-size: 0.72rem;
	z-index: 1;
}

.fa-org-connector-down {
	width: 2px;
	height: 16px;
	background: #1a5276;
	flex-shrink: 0;
}

.fa-org-connector-root {
	height: 18px;
	position: relative;
	z-index: 2;
	margin-bottom: -1px;
}

.fa-org-row {
	display: flex;
	justify-content: center;
	align-items: flex-start;
	position: relative;
}

.fa-org-row-lines {
	gap: 0;
	width: 560px;
	max-width: 100%;
	min-width: 0;
}

.fa-org-row-lines > .fa-org-col {
	flex: 1 1 0;
	padding-top: 16px;
	min-width: 0;
}

.fa-org-row-lines::before {
	content: '';
	position: absolute;
	top: 0;
	left: 25%;
	right: 25%;
	height: 2px;
	background: #1a5276;
	z-index: 1;
}

.fa-org-row-branches {
	width: 100%;
	gap: 0;
	padding-top: 0;
	justify-content: center;
}

.fa-org-row-branches > .fa-org-col {
	flex: 1 1 0;
	padding-top: 16px;
	min-width: 0;
}

.fa-org-row-branches::before {
	content: '';
	position: absolute;
	top: 0;
	left: 25%;
	right: 25%;
	height: 2px;
	background: #1a5276;
	z-index: 1;
}

.fa-org-col {
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
	flex: 0 0 auto;
}

.fa-org-col::before {
	content: '';
	position: absolute;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	width: 2px;
	height: 16px;
	background: #1a5276;
	z-index: 1;
}

.fa-org-col-branch {
	align-items: center;
	width: auto;
	min-width: 0;
	flex: 1 1 0;
	padding-bottom: 8px;
	overflow: visible;
}

.fa-org-branch-tree {
	position: relative;
	margin-top: 0;
	padding-top: 12px;
	width: max-content;
	margin-left: 50%;
}

.fa-org-eq-list {
	list-style: none;
	margin: 0;
	padding: 0;
	position: relative;
	width: max-content;
}

.fa-org-eq-li {
	position: relative;
	padding: 10px 0 0 16px;
}

/* Horizontal arm joins the node */
.fa-org-eq-li::before {
	content: '';
	position: absolute;
	left: 0;
	top: 20px;
	width: 16px;
	height: 2px;
	background: #1a5276;
	z-index: 1;
}

/* Vertical stem — default: stop at this node's arm */
.fa-org-eq-li::after {
	content: '';
	position: absolute;
	left: 0;
	top: 0;
	width: 2px;
	height: 22px;
	background: #1a5276;
	z-index: 0;
}

/* First child: extend up to meet the parent drop */
.fa-org-eq-li:first-child::after {
	top: -12px;
	height: 34px;
}

/* Non-last children: full height through this item */
.fa-org-eq-li:not(:last-child)::after {
	height: 100%;
}

/* First + not last: full height and meet parent drop */
.fa-org-eq-li:first-child:not(:last-child)::after {
	top: -12px;
	height: calc(100% + 12px);
}

.fa-calculations-head {
	padding: 18px 22px;
	border-bottom: 1px solid rgba(15, 23, 42, 0.08);
	background: linear-gradient(180deg, rgba(248, 250, 252, 1) 0%, rgba(255, 255, 255, 1) 100%);
}

.fa-recalc-all-btn {
	font-weight: 600;
}

.fa-calculations-body {
	padding: 18px 22px;
}

.fa-calc-actions {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	align-items: center;
	gap: 6px;
}

.fa-calc-icon-btn {
	width: 31px;
	height: 31px;
	padding: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.fa-equation-chart-modal {
	z-index: 1100;
}

.fa-equation-chart-modal-dialog {
	max-width: 960px;
	width: calc(100vw - 48px);
}

.fa-equation-chart-modal-body {
	overflow: visible;
	padding: 12px 24px 28px;
}

.fa-equation-chart-container {
	width: 100%;
	min-height: 420px;
}

.fa-calc-value {
	font-size: 1.125rem;
	font-weight: 550;
	line-height: 1.3;
}

.fa-calc-value-green {
	color: #16a34a;
}

.fa-calc-value-yellow {
	color: #ca8a04;
}

.fa-calc-value-red {
	color: #dc2626;
}

.fa-filters {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 10px;
}

.fa-filter-group {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.fa-filter-label {
	font-size: 0.65rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #94a3b8;
}

.fa-main-card-header .fa-filter-label {
	color: #94a3b8;
}

.fa-main-card-header .fa-filter-search {
	min-width: 158px;
}

.fa-main-card-header .fa-filter-search :deep(.search-selected) {
	border-radius: 6px;
	border: 1px solid rgba(255, 255, 255, 0.22);
	background: rgba(255, 255, 255, 0.96);
	color: #0f172a;
	font-size: 0.8rem;
	padding: 6px 10px;
	min-height: 34px;
}

.fa-main-card-header .fa-filter-search :deep(.invalid-feedback) {
	color: #fecaca;
	font-size: 0.72rem;
}

.fa-filter-input {
	padding: 6px 10px;
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 6px;
	background: rgba(255, 255, 255, 0.08);
	color: #fff;
	font-size: 0.8rem;
	min-width: 130px;
	transition: border-color 0.2s, background 0.2s;
}

.fa-filter-input:hover,
.fa-filter-input:focus {
	outline: none;
	border-color: rgba(56, 189, 248, 0.5);
	background: rgba(255, 255, 255, 0.12);
}

.fa-filter-btn {
	padding: 6px 14px;
	border: none;
	border-radius: 6px;
	background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%);
	color: #0f172a;
	font-size: 0.8rem;
	font-weight: 600;
	cursor: pointer;
	transition: transform 0.2s, box-shadow 0.2s;
	box-shadow: 0 2px 8px rgba(14, 165, 233, 0.35);
}

.fa-filter-btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 6px 20px rgba(14, 165, 233, 0.5);
}

.fa-filter-btn:active {
	transform: translateY(0);
}

.fa-section-title {
	margin: 0 0 16px 0;
	font-size: 1.1rem;
	font-weight: 700;
	color: #0f172a;
	letter-spacing: -0.01em;
}

.fa-main-card-body .fa-summary-block,
.fa-main-card-body .fa-chart-block {
	background: transparent;
	padding: 0;
	box-shadow: none;
	border: none;
}

.fa-summary-fan-wrap .fa-tile {
	width: auto;
	min-width: 88px;
	max-width: 128px;
	padding: 16px 12px;
}

.fa-tile-label-wrap {
	font-size: 0.58rem;
	line-height: 1.2;
	text-align: center;
	white-space: normal;
	word-break: break-word;
}

.fa-tile-fan-power {
	background: linear-gradient(145deg, #b8b5b0 0%, #8e8b87 100%);
	cursor: default;
}

.fa-tile-fan-power .fa-tile-value {
	color: #ffffff;
}

.fa-tile-fan-power .fa-tile-label {
	color: rgba(255, 255, 255, 0.92);
}

.fa-tile-fan-power .fa-tile-value.fa-calc-value-green {
	color: #bbf7d0;
}

.fa-tile-fan-power .fa-tile-value.fa-calc-value-yellow {
	color: #fef08a;
}

.fa-tile-fan-power .fa-tile-value.fa-calc-value-red {
	color: #fecaca;
}

.fa-tiles {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.fa-tiles-inline {
	flex-direction: row;
	flex-wrap: wrap;
	gap: 10px;
}

.fa-tiles-inline .fa-tile {
	width: auto;
	flex: 1;
	min-width: 70px;
}

.fa-tile {
	width: 100%;
	padding: 16px 18px;
	border-radius: 14px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	transition: transform 0.2s, box-shadow 0.2s;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.fa-tile:hover {
	transform: translateY(-2px);
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.fa-tile-value {
	font-size: 1.75rem;
	font-weight: 800;
	color: #fff;
	letter-spacing: -0.02em;
	line-height: 1.2;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.fa-tile-label {
	font-size: 0.8rem;
	font-weight: 600;
	color: rgba(255, 255, 255, 0.9);
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.fa-tile-open {
	background: linear-gradient(145deg, #ef4444 0%, #dc2626 100%);
}

.fa-tile-closed {
	background: linear-gradient(145deg, #22c55e 0%, #16a34a 100%);
}

.fa-tile-total {
	background: linear-gradient(145deg, #3b82f6 0%, #2563eb 100%);
}

.fa-tile-clickable {
	cursor: pointer;
	border: none;
	font: inherit;
	text-align: center;
	appearance: none;
	-webkit-appearance: none;
	width: 100%;
}

.fa-tile-clickable:focus-visible {
	outline: 2px solid #38bdf8;
	outline-offset: 3px;
}

.fa-tile-selected {
	box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.55), 0 8px 24px rgba(0, 0, 0, 0.12);
}

.fa-pie-wrap {
	min-height: 280px;
	width: 100%;
}

.fa-pipeline-section {
	margin-bottom: 24px;
}

.fa-pipeline-card {
	background: #fff;
	border-radius: 16px;
	padding: 24px 28px;
	box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
	border: 1px solid rgba(15, 23, 42, 0.06);
}

.fa-pipeline-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
	margin-bottom: 10px;
}

.fa-pipeline-header .fa-section-title {
	margin-bottom: 0;
}

.fa-pipeline-header-right {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
}

.fa-pipeline-days-pill {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: #64748b;
	background: #f1f5f9;
	padding: 5px 12px;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
}

.fa-pipeline-close {
	padding: 6px 14px;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #f8fafc;
	font-size: 0.8rem;
	font-weight: 600;
	color: #475569;
	cursor: pointer;
}

.fa-pipeline-close:hover {
	background: #f1f5f9;
	color: #0f172a;
}

.fa-pipeline-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 16px 24px;
	margin-bottom: 18px;
	padding-bottom: 14px;
	border-bottom: 1px solid #e2e8f0;
}

.fa-pipeline-legend-item {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 0.8rem;
	font-weight: 500;
	color: #475569;
}

.fa-pipeline-legend-dot {
	width: 12px;
	height: 12px;
	border-radius: 4px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.fa-pipeline-legend-done {
	background: linear-gradient(145deg, #22c55e, #16a34a);
}

.fa-pipeline-legend-pending {
	background: linear-gradient(145deg, #f87171, #dc2626);
}

.fa-pipeline-legend-remain {
	background: #94a3b8;
}

.fa-pipeline-loading {
	margin: 0;
	padding: 20px;
	text-align: center;
	color: #64748b;
	font-size: 0.9rem;
}

.fa-pipeline-stage-head-row {
	display: grid;
	grid-template-columns: minmax(180px, 260px) 1fr 52px;
	align-items: stretch;
	gap: 14px;
	margin-bottom: 2px;
}

.fa-pipeline-stage-head-spacer {
	min-height: 8px;
}

.fa-pipeline-track-headings {
	display: flex;
	flex-direction: row;
	align-items: stretch;
	background: transparent;
	border-radius: 8px;
	border: 1px dashed rgba(148, 163, 184, 0.45);
	min-height: 44px;
}

.fa-pipeline-heading-cell {
	flex: 1 1 0;
	min-width: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 6px 4px;
	font-size: 0.68rem;
	font-weight: 700;
	color: #334155;
	line-height: 1.25;
	letter-spacing: 0.01em;
}

.fa-pipeline-heading-total {
	min-height: 8px;
}

.fa-pipeline-body {
	display: flex;
	flex-direction: column;
	gap: 18px;
}

.fa-pipeline-group {
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding-bottom: 14px;
	border-bottom: 1px solid #e2e8f0;
}

.fa-pipeline-group:last-child {
	border-bottom: none;
	padding-bottom: 0;
}

.fa-pipeline-row {
	display: grid;
	grid-template-columns: minmax(180px, 260px) 1fr 52px;
	align-items: center;
	gap: 14px;
	animation: faFadeIn 0.4s ease backwards;
}

.fa-pipeline-row-rework .fa-pipeline-job {
	padding-left: 18px;
	border-left: 3px solid #cbd5e1;
	margin-left: 4px;
}

.fa-pipeline-rework-label {
	color: #475569 !important;
	font-weight: 600;
}

@media (max-width: 900px) {
	.fa-pipeline-row {
		grid-template-columns: 1fr;
	}

	.fa-pipeline-stage-head-row {
		grid-template-columns: 1fr;
	}

	.fa-pipeline-stage-head-spacer {
		display: none;
	}

	.fa-pipeline-track-headings {
		flex-wrap: wrap;
		gap: 4px;
		padding: 6px;
	}

	.fa-pipeline-heading-cell {
		flex: 1 1 42%;
		min-width: 0;
		font-size: 0.62rem;
	}

	.fa-pipeline-heading-total {
		display: none;
	}

	.fa-pipeline-total {
		text-align: left;
	}

}

@keyframes faFadeIn {
	from {
		opacity: 0;
		transform: translateY(6px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.fa-pipeline-job {
	display: flex;
	flex-direction: column;
	gap: 4px;
	justify-content: center;
}

.fa-pipeline-id {
	font-weight: 700;
	font-size: 0.92rem;
}

.fa-pipeline-id-open {
	color: #dc2626;
}

.fa-pipeline-id-done {
	color: #15803d;
}

.fa-pipeline-name {
	font-size: 0.8rem;
	color: #64748b;
	line-height: 1.35;
}

.fa-pipeline-track {
	width: 100%;
}

.fa-pipeline-track-bar {
	display: flex;
	flex-direction: row;
	border-radius: 10px;
	overflow: hidden;
	background: #f1f5f9;
	box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.fa-pipeline-track-bar-closed {
	min-height: 52px;
	height: 52px;
}

.fa-pipeline-track-bar-open {
	min-height: 56px;
	height: auto;
}

.fa-pipeline-seg-stack {
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding: 8px 4px;
	height: 100%;
	min-height: 56px;
	gap: 2px;
}

.fa-pipeline-seg-equal {
	flex: 1 1 0;
	min-width: 0;
}

.fa-pipeline-seg {
	min-width: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3px 6px;
	font-weight: 700;
	color: #fff;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
	box-sizing: border-box;
	gap: 0;
	line-height: 1.15;
}

.fa-pipeline-seg-days {
	font-size: 0.72rem;
	flex-shrink: 0;
}

.fa-pipeline-seg-state {
	font-size: 0.65rem;
	flex-shrink: 0;
	opacity: 0.95;
}

.fa-pipeline-seg-date {
	font-size: 0.52rem;
	font-weight: 600;
	opacity: 0.92;
	letter-spacing: 0.01em;
	white-space: nowrap;
}

.fa-pipeline-total {
	font-size: 0.9rem;
	font-weight: 800;
	color: #0f172a;
	text-align: center;
}

.fa-pipeline-total-na {
	color: #94a3b8;
	font-weight: 600;
}

.fa-pipeline-cell-label {
	font-size: 0.65rem;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	opacity: 0.95;
	text-align: center;
	line-height: 1.2;
}

.fa-pipeline-cell-state {
	font-size: 0.72rem;
}

.fa-pipeline-cell-completed {
	background: linear-gradient(145deg, #22c55e, #16a34a);
}

.fa-pipeline-cell-pending {
	background: linear-gradient(145deg, #f87171, #dc2626);
}

.fa-pipeline-cell-remaining {
	background: linear-gradient(180deg, #b0b9c5, #94a3b8);
	color: #fff;
}

.fa-empty-tight {
	padding: 18px 22px;
}

.fa-empty {
	margin: 0;
	padding: 24px;
	text-align: center;
	color: #64748b;
	font-size: 0.9rem;
}

/* —— Month horizontal day strip —— */
.fa-month-strip-card {
	background: #fff;
	border-radius: 16px;
	margin-bottom: 24px;
	padding: 20px 22px 18px;
	box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
	border: 1px solid rgba(15, 23, 42, 0.06);
}

.fa-month-strip-head {
	margin-bottom: 14px;
}

.fa-month-strip-title {
	margin-bottom: 8px;
}

.fa-month-strip-sub {
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 6px;
	font-size: 0.78rem;
	color: #64748b;
	line-height: 1.45;
}

.fa-month-strip-kpi strong {
	color: #0f172a;
}

.fa-month-strip-loading {
	margin: 0;
	padding: 12px 0;
	color: #64748b;
	font-size: 0.85rem;
}

.fa-month-strip-scroll {
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	gap: 8px;
	overflow-x: auto;
	padding-bottom: 6px;
	-webkit-overflow-scrolling: touch;
	scrollbar-width: thin;
}

.fa-month-day-col {
	flex: 0 0 auto;
	width: 56px;
	min-height: 108px;
	padding: 8px 4px 10px;
	border-radius: 10px;
	border: 1px solid #e2e8f0;
	background: #f8fafc;
	cursor: pointer;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	font-family: inherit;
	transition: border-color 0.15s, background 0.15s, transform 0.12s;
}

.fa-month-day-col:hover {
	border-color: #38bdf8;
	background: #eff6ff;
	transform: translateY(-1px);
}

.fa-month-day-col-today {
	border-color: #0ea5e9;
	box-shadow: 0 0 0 1px rgba(14, 165, 233, 0.25);
}

.fa-month-day-col-selected {
	background: #fef2f2;
	border-color: #f87171;
}

.fa-month-day-col-static {
	cursor: default;
	opacity: 0.9;
}

.fa-month-day-col-static:hover {
	border-color: #e2e8f0;
	background: #f8fafc;
	transform: none;
}

.fa-month-day-dow {
	font-size: 0.58rem;
	font-weight: 700;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.fa-month-day-num {
	font-size: 1rem;
	font-weight: 800;
	color: #0f172a;
	line-height: 1;
}

.fa-month-day-remain {
	font-size: 0.95rem;
	font-weight: 800;
	color: #dc2626;
	margin-top: 2px;
}

.fa-month-day-dev {
	font-size: 0.58rem;
	font-weight: 700;
	color: #b45309;
	margin-top: auto;
}

.fa-pipeline-row-clickable {
	cursor: pointer;
}

.fa-pipeline-row-clickable:hover {
	background: rgba(248, 250, 252, 0.85);
	border-radius: 10px;
}

.fa-pipeline-seg-interactive {
	cursor: pointer;
	position: relative;
}

.fa-pipeline-seg-interactive:hover {
	filter: brightness(1.06);
}

.fa-stage-tooltip {
	position: fixed;
	z-index: 1080;
	pointer-events: none;
	min-width: 140px;
	max-width: 260px;
	padding: 10px 12px;
	border-radius: 8px;
	background: #0f172a;
	color: #f8fafc;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.35);
	font-size: 0.78rem;
	line-height: 1.45;
}

.fa-stage-tooltip-heading {
	font-size: 0.65rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #94a3b8;
	margin-bottom: 4px;
}

.fa-stage-tooltip-line {
	font-weight: 600;
}

.fa-detail-summary {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.fa-detail-row {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
}

.fa-detail-label {
	font-size: 0.75rem;
	font-weight: 700;
	color: #64748b;
	min-width: 90px;
}

.fa-detail-value {
	font-size: 0.85rem;
	color: #0f172a;
}

.fa-detail-status {
	font-weight: 600;
	font-size: 0.8rem;
}

.fa-detail-status-completed {
	color: #15803d;
}

.fa-detail-status-pending {
	color: #dc2626;
}

.fa-detail-status-remaining {
	color: #64748b;
}
</style>